import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import {
  FlightRawEventName,
  useEmitCoreFlightRawEvent,
  FlightSearchActions,
  FlightSearchSelectors,
  FlightShopSelectors,
  type FlightSearchValues,
  type FormErrors,
} from "@b2bportal/core-flights";
import type { IPassengerCounts, ITripTerminus } from "@b2bportal/core-types";
import {
  FLIGHTS_SHOP_DEPARTURE_PATH,
  FLIGHTS_SHOP_MULTICITY_PATH,
  FLIGHT_SEARCH_CALENDAR_HASH,
  maybeStartFunnel,
} from "@b2bportal/core-utilities";
import { ChangeTripTypeScreenEnum } from "@hopper-b2b/types";
import { TripCategory } from "@overrides/types";
import {
  type BuildFlightShopQueryArgs,
  buildFlightShopQuery,
  getFlightFunnelSearch,
} from "@tenant/flights";

export type ActiveSearchContext = FlightSearchValues & {
  isSearchComplete: boolean;
  isSearchCompleteWithoutDates: boolean;
  openCalendar: boolean;
  validSearchParams?: BuildFlightShopQueryArgs;
  formError: FormErrors;
};

export type ActiveSearchHandlers = {
  onOriginChange: (origin: ITripTerminus) => void;
  onDestinationChange: (dest: ITripTerminus) => void;
  onOriginFocus: () => void;
  onDestinationFocus: () => void;
  onDepartureDateChange: (date?: string) => void;
  onReturnDateChange: (date?: string) => void;
  onPassengerCountChange: (pax: IPassengerCounts) => void;
  onTripCategoryChange: (tripCat: TripCategory) => void;
  onSearch: (
    params: BuildFlightShopQueryArgs,
    tripCategory: TripCategory
  ) => void;
  onSearchReset: () => void;
  onSubmitForm: () => void;
  onNavigateToCalendar: () => void;
  onCalendarClose: () => void;
  onSwapOriginDestination: () => void;
  onMulticityOriginChange: (origin: ITripTerminus, index: number) => void;
  onMulticityDestinationChange: (
    destination: ITripTerminus,
    index: number
  ) => void;
  onMulticityOriginFocus: (index: number) => void;
  onMulticityDestinationFocus: (index: number) => void;
  onMulticityDepartureDateChange: (
    departureDate: string,
    index: number
  ) => void;
  onMulticityReturnDateChange: (returnDate: string, index: number) => void;
  onAddMulticityRoute: () => void;
  onRemoveMulticityRoute: (index: number) => void;
};

export const useFlightSearch = (
  index?: number
): {
  context: ActiveSearchContext;
  handlers: ActiveSearchHandlers;
} => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const emitChangedTripTypeEvent = useEmitCoreFlightRawEvent(
    FlightRawEventName.ChangedTripType
  );

  const searchState = useSelector(FlightSearchSelectors.getSearchState);
  const {
    origin,
    destination,
    departureDate,
    returnDate,
    tripCategory,
    passengerCounts,
    tripFilter,
    multicityRoutes,
  } = useSelector(FlightSearchSelectors.getSearchFormValues);
  const isError = useSelector(FlightShopSelectors.getFlightsErrorState);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const formError = useSelector(FlightSearchSelectors.getFormError);

  const onSearch = useCallback(
    (params: BuildFlightShopQueryArgs, tripCategory: TripCategory) => {
      // If there's an error, reset search fields to force isSameSearch to return false
      if (isError) {
        dispatch(FlightSearchActions.resetSearchForRefetch());
      }

      const shopQuery = buildFlightShopQuery(params);
      maybeStartFunnel(
        "air",
        getFlightFunnelSearch(location.search),
        getFlightFunnelSearch(shopQuery)
      );
      if (tripCategory === TripCategory.MULTI_CITY) {
        navigate(`${FLIGHTS_SHOP_MULTICITY_PATH(0)}?${shopQuery}`);
      } else {
        navigate(`${FLIGHTS_SHOP_DEPARTURE_PATH}?${shopQuery}`);
      }
    },
    [navigate, isError, dispatch, location.search]
  );

  const onSearchReset = useCallback(() => {
    dispatch(FlightSearchActions.setSearchFormValues(searchState));
  }, [dispatch, searchState]);

  const onDepartureDateChange = useCallback(
    (departureDate?: string) => {
      dispatch(FlightSearchActions.setSearchFormDepartureDate(departureDate));
      dispatch(FlightSearchActions.clearDatesError());
    },
    [dispatch]
  );
  const onReturnDateChange = useCallback(
    (returnDate?: string) => {
      dispatch(FlightSearchActions.setSearchFormReturnDate(returnDate));
      dispatch(FlightSearchActions.clearDatesError());
    },
    [dispatch]
  );
  const onPassengerCountChange = (passengerCount: IPassengerCounts) =>
    dispatch(FlightSearchActions.setSearchFormPassengerCount(passengerCount));

  const onTripCategoryChange = (tripCategory: TripCategory) => {
    dispatch(FlightSearchActions.setSearchFormTripCategory(tripCategory));
    emitChangedTripTypeEvent({
      properties: { screen: ChangeTripTypeScreenEnum.LOCATION_PICKER },
    });
  };

  const onOriginChange = useCallback(
    (origin: ITripTerminus) => {
      dispatch(FlightSearchActions.setSearchFormOrigin(origin));
      dispatch(FlightSearchActions.clearOriginError());
    },
    [dispatch]
  );

  const onDestinationChange = useCallback(
    (destination: ITripTerminus) => {
      dispatch(FlightSearchActions.setSearchFormDestination(destination));
      dispatch(FlightSearchActions.clearDestinationError());
    },
    [dispatch]
  );

  // Clear errors when user focuses/starts typing in the field
  const onOriginFocus = useCallback(() => {
    dispatch(FlightSearchActions.clearOriginError());
  }, [dispatch]);

  const onDestinationFocus = useCallback(() => {
    dispatch(FlightSearchActions.clearDestinationError());
  }, [dispatch]);

  // Multicity handlers
  const onMulticityOriginChange = useCallback(
    (origin: ITripTerminus, index: number) => {
      dispatch(
        FlightSearchActions.setSearchFormMulticityOrigin({ origin, index })
      );
      dispatch(FlightSearchActions.clearMulticityOriginError(index));
    },
    [dispatch]
  );

  const onMulticityDestinationChange = useCallback(
    (destination: ITripTerminus, index: number) => {
      dispatch(
        FlightSearchActions.setSearchFormMulticityDestination({
          destination,
          index,
        })
      );
      dispatch(FlightSearchActions.clearMulticityDestinationError(index));
    },
    [dispatch]
  );

  const onMulticityOriginFocus = useCallback(
    (index: number) => {
      dispatch(FlightSearchActions.clearMulticityOriginError(index));
    },
    [dispatch]
  );

  const onMulticityDestinationFocus = useCallback(
    (index: number) => {
      dispatch(FlightSearchActions.clearMulticityDestinationError(index));
    },
    [dispatch]
  );
  const onMulticityDepartureDateChange = useCallback(
    (departureDate: string, index: number) => {
      dispatch(
        FlightSearchActions.setSearchFormMulticityDepartureDate({
          departureDate,
          index,
        })
      );
      dispatch(FlightSearchActions.clearMulticityDatesError(index));
    },
    [dispatch]
  );
  const onMulticityReturnDateChange = useCallback(
    (returnDate: string, index: number) => {
      dispatch(
        FlightSearchActions.setSearchFormMulticityReturnDate({
          returnDate,
          index,
        })
      );
      dispatch(FlightSearchActions.clearMulticityDatesError(index));
    },
    [dispatch]
  );

  const onAddMulticityRoute = () => {
    dispatch(FlightSearchActions.addMulticityRoute());
  };

  const onRemoveMulticityRoute = (index: number) => {
    dispatch(FlightSearchActions.removeMulticityRoute(index));
  };

  const onSwapOriginDestination = useCallback(() => {
    // Only swap for non-multicity trips when both fields are filled
    if (tripCategory !== TripCategory.MULTI_CITY && origin && destination) {
      dispatch(FlightSearchActions.setSearchFormOrigin(destination));
      dispatch(FlightSearchActions.setSearchFormDestination(origin));
      dispatch(FlightSearchActions.clearOriginError());
      dispatch(FlightSearchActions.clearDestinationError());
    }
  }, [dispatch, tripCategory, origin, destination]);

  const validSearchParams = validateSearchParams({
    origin,
    destination,
    departureDate,
    returnDate,
    tripCategory,
    passengerCounts,
    tripFilter,
    multicityRoutes,
  });

  const isSearchCompleteWithoutDates =
    origin != null && destination != null && tripCategory != null;

  const isSearchComplete = validSearchParams != null;

  useEffect(() => {
    if (location.hash === FLIGHT_SEARCH_CALENDAR_HASH) {
      if (isSearchCompleteWithoutDates) {
        setOpenCalendar(true);
      } else {
        navigate(`${location.pathname}${location.search}`, { replace: true });
      }
    } else {
      setOpenCalendar(false);
    }
  }, [location, isSearchCompleteWithoutDates, navigate]);

  const onCalendarClose = useCallback(() => {
    navigate(`${location.pathname}${location.search}`, { replace: true });
    navigate(-1);
  }, [navigate, location]);

  const onNavigateToCalendar = useCallback(() => {
    navigate(
      `${location.pathname}${location.search}${FLIGHT_SEARCH_CALENDAR_HASH}`
    );
  }, [navigate, location]);

  const onSubmitForm = useCallback(() => {
    // Reuse the same validation logic as isSearchComplete
    if (!validSearchParams) {
      // Set errors on missing fields based on trip category
      const errors: FormErrors = {};

      if (tripCategory === TripCategory.MULTI_CITY) {
        errors.multicityRoutes = multicityRoutes.map((route) => {
          const routeErrors = {
            origin: !route.origin ? { message: undefined } : undefined,
            destination: !route.destination
              ? { message: undefined }
              : undefined,
            dates: !route.departureDate ? { message: undefined } : undefined,
          };
          // Only include route errors if there are actual errors
          return routeErrors.origin ||
            routeErrors.destination ||
            routeErrors.dates
            ? routeErrors
            : undefined;
        });
      } else {
        if (!origin) {
          errors.origin = { message: undefined };
        }
        if (!destination) {
          errors.destination = { message: undefined };
        }
        if (
          !departureDate ||
          (tripCategory === TripCategory.ROUND_TRIP && !returnDate)
        ) {
          errors.dates = { message: undefined };
        }
      }

      dispatch(FlightSearchActions.setFormError(errors));
      return;
    }

    // All validations passed - execute the search
    onSearch(validSearchParams, tripCategory);
  }, [
    validSearchParams,
    tripCategory,
    onSearch,
    origin,
    destination,
    departureDate,
    returnDate,
    multicityRoutes,
    dispatch,
  ]);

  const isMulticitySearch =
    index != null &&
    multicityRoutes[index] != null &&
    tripCategory === TripCategory.MULTI_CITY;

  return {
    context: {
      origin: isMulticitySearch ? multicityRoutes[index]?.origin : origin,
      destination: isMulticitySearch
        ? multicityRoutes[index]?.destination
        : destination,
      tripCategory,
      passengerCounts,
      departureDate: isMulticitySearch
        ? multicityRoutes[index]?.departureDate
        : departureDate,
      returnDate:
        tripCategory === TripCategory.ROUND_TRIP ? returnDate : undefined,
      isSearchComplete,
      isSearchCompleteWithoutDates,
      openCalendar,
      validSearchParams,
      tripFilter,
      multicityRoutes,
      formError,
    },
    handlers: {
      onSearch,
      onSearchReset,
      onSubmitForm,
      onTripCategoryChange,
      onDepartureDateChange: isMulticitySearch
        ? (departureDate: string | undefined) => {
            if (departureDate != null) {
              onMulticityDepartureDateChange(departureDate, index);
            }
          }
        : onDepartureDateChange,
      onReturnDateChange: isMulticitySearch
        ? (returnDate: string | undefined) => {
            if (returnDate != null) {
              onMulticityReturnDateChange(returnDate, index);
            }
          }
        : onReturnDateChange,
      onDestinationChange: isMulticitySearch
        ? (destination: ITripTerminus) =>
            onMulticityDestinationChange(destination, index)
        : onDestinationChange,
      onOriginChange: isMulticitySearch
        ? (origin: ITripTerminus) => onMulticityOriginChange(origin, index)
        : onOriginChange,
      onOriginFocus: isMulticitySearch
        ? () => onMulticityOriginFocus(index)
        : onOriginFocus,
      onDestinationFocus: isMulticitySearch
        ? () => onMulticityDestinationFocus(index)
        : onDestinationFocus,
      onPassengerCountChange,
      onNavigateToCalendar,
      onCalendarClose,
      onSwapOriginDestination,
      onMulticityOriginChange,
      onMulticityDestinationChange,
      onMulticityOriginFocus,
      onMulticityDestinationFocus,
      onMulticityDepartureDateChange,
      onMulticityReturnDateChange,
      onAddMulticityRoute,
      onRemoveMulticityRoute,
    },
  };
};

const validateSearchParams = ({
  origin,
  destination,
  departureDate,
  returnDate,
  tripCategory,
  passengerCounts,
  multicityRoutes,
}: ReturnType<typeof FlightSearchSelectors.getSearchFormValues>):
  | BuildFlightShopQueryArgs
  | undefined => {
  if (tripCategory === TripCategory.MULTI_CITY) {
    if (
      multicityRoutes == null ||
      multicityRoutes.length === 0 ||
      multicityRoutes.some(
        (route) =>
          route.departureDate == null ||
          route.destination == null ||
          route.origin == null
      )
    ) {
      return undefined;
    }

    return {
      multicityRoutes,
      tripCategory,
      passengerCounts,
    };
  }

  if (departureDate == null || destination == null || origin == null) {
    return undefined;
  }
  if (tripCategory === TripCategory.ONE_WAY) {
    return {
      origin,
      destination,
      departureDate,
      tripCategory,
      passengerCounts,
    };
  }

  if (returnDate == null) return undefined;
  return {
    origin,
    destination,
    departureDate,
    returnDate,
    tripCategory,
    passengerCounts,
  };
};
